import React, { useCallback, useState } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { TobaccoClass } from '@getethos/ethos-global-lib';
import InfoIcon from '@mui/icons-material/Info';
import Tooltip from '@mui/material/Tooltip';
import { DecisionIcon } from '../../../common/components/DecisionIcon';
import {
  Decision,
  RATE_CLASS_DISPLAY_TEXT,
  TOBACCO_CLASS_DISPLAY_TEXT,
  UpdatedRateClass,
} from '../../../common/constants/decision';
import { IRule, IRuleSet } from '../interfaces/IRuleResults';
import { Conditions } from './Conditions';

const RuleAccordion = ({
  ruleSet,
  isRidersTab,
}: {
  ruleSet: IRuleSet;
  isRidersTab?: boolean;
}) => {
  const displayRuleSetName = ruleSet.ruleSetName
    .replace(/\s*\(workflow\)\s*/gi, ' ')
    .trim();

  // Only expanded when it is a guideline rule set
  const shouldShow = ruleSet.matchedRules.some(
    rule =>
      !(
        !rule.decision &&
        !rule.classDowngrade &&
        !rule.debits &&
        !(rule.ineligibleProduct ?? []).length &&
        !(rule.ineligibleRiders ?? []).length &&
        !rule.maxCoverage &&
        !(rule.riskName ?? []).length &&
        !rule.tobaccoClass &&
        !(rule.requirement ?? []).length
      ),
  );

  const [expanded, setExpanded] = useState(shouldShow);

  const handleToggle = () => setExpanded(!expanded);
  const getTotalClassDowngrade = (rules: IRule[]) => {
    return rules.reduce((total, rule) => total + (rule.classDowngrade || 0), 0);
  };

  const getTooltipContent = useCallback((rule: IRule) => {
    const { percentageAddition, requirement } = rule;
    if (!percentageAddition && requirement.length === 0) return null;
    const rows: string[] = [];

    if (percentageAddition)
      rows.push(`outcome_percentage_addition: ${percentageAddition}`);
    if (requirement.length > 0)
      rows.push(`outcome_requirements: ${requirement.join(', ')}`);

    return rows.join('\n');
  }, []);

  return (
    <Accordion
      expanded={expanded}
      onChange={handleToggle}
      sx={{
        boxShadow: 1,
        border: `1px solid #f3f3f3`,
        '&:not(:last-child)': {
          borderBottom: 0,
        },
        '&::before': {
          display: 'none',
        },
        '&.Mui-expanded': {
          marginBlock: 0,
        },
      }}
    >
      <AccordionSummary
        sx={{
          gap: 2,
          backgroundColor: 'rgba(0, 0, 0, .03)',
          '&.MuiAccordionSummary-root': {},
          '& .MuiAccordionSummary-content': {
            margin: 0,
            marginBlock: 2,
          },
        }}
        expandIcon={<ExpandMoreIcon />}
      >
        <div style={{ display: 'grid', gap: '2px' }}>
          <Typography variant="h3">{displayRuleSetName}</Typography>
          {ruleSet.outcome.decision ? (
            ruleSet.outcome.decision === 'Approved' ? (
              <>
                {ruleSet.outcome.rateClass && (
                  <Typography variant="body2" color="text.secondary">
                    Rate Class:{' '}
                    {
                      RATE_CLASS_DISPLAY_TEXT[
                        ruleSet.outcome.rateClass as UpdatedRateClass
                      ]
                    }
                  </Typography>
                )}
                {ruleSet.matchedRules.some(rule =>
                  Boolean(rule.classDowngrade),
                ) && (
                  <Typography variant="body2" color="text.secondary">
                    Class Downgrade:{' '}
                    {getTotalClassDowngrade(ruleSet.matchedRules)}
                  </Typography>
                )}

                {ruleSet.outcome.debits && (
                  <Typography variant="body2" color="text.secondary">
                    Debits: {ruleSet.outcome.debits}
                  </Typography>
                )}
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Decision: {ruleSet.outcome.decision}
              </Typography>
            )
          ) : null}
        </div>
      </AccordionSummary>

      <AccordionDetails
        sx={{
          width: '100%',
          paddingInline: 0,
          paddingBlock: 1,
          borderTop: '1px solid rgba(0, 0, 0, .125)',
        }}
      >
        {!isRidersTab ? (
          <Box sx={{ width: '100%', overflowX: 'auto' }}>
            <Table
              size="small"
              sx={{
                tableLayout: 'fixed',
                width: 'auto',
              }}
            >
              <TableBody>
                {ruleSet.matchedRules.length > 0 ? (
                  ruleSet.matchedRules.map((rule, ruleIdx) => (
                    <TableRow key={ruleIdx} sx={{ borderBottom: 'none' }}>
                      <TableCell sx={{ width: '40%', borderBottom: 'none' }}>
                        <Conditions data={rule.conditions} />
                      </TableCell>
                      <TableCell
                        sx={{
                          width: '100px',
                          borderBottom: 'none',
                          textAlign: 'center',
                        }}
                      >
                        <DecisionIcon decision={rule.decision as Decision} />
                      </TableCell>
                      <TableCell sx={{ width: '15%', borderBottom: 'none' }}>
                        <Typography variant="body2" color="text.secondary">
                          {[
                            RATE_CLASS_DISPLAY_TEXT[
                              rule.rateClass as UpdatedRateClass
                            ],
                            rule.classDowngrade && `+${rule.classDowngrade}`,
                          ]
                            .filter(Boolean)
                            .join(' ')}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ width: '10%', borderBottom: 'none' }}>
                        <Typography variant="body2" color="text.secondary">
                          {rule.debits || ''}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ width: '15%', borderBottom: 'none' }}>
                        <Typography variant="body2" color="text.secondary">
                          {TOBACCO_CLASS_DISPLAY_TEXT[
                            rule.tobaccoClass as TobaccoClass
                          ] || ''}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ width: '15%', borderBottom: 'none' }}>
                        <Typography variant="body2" color="text.secondary">
                          {rule.maxCoverage || ''}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ width: '5%', borderBottom: 'none' }}>
                        {!!getTooltipContent(rule) && (
                          <Tooltip
                            placement="top"
                            title={getTooltipContent(rule)}
                          >
                            <InfoIcon />
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      sx={{ textAlign: 'center', p: 2, borderBottom: 'none' }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        No matching rules found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        ) : (
          <Box sx={{ width: '100%', overflowX: 'auto' }}>
            <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '60%' }}>Conditions</TableCell>
                  <TableCell sx={{ width: '40%' }}>Ineligible Riders</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ruleSet.matchedRules.length > 0 ? (
                  ruleSet.matchedRules.map((rule, ruleIdx) => (
                    <TableRow key={ruleIdx} sx={{ borderBottom: 'none' }}>
                      <TableCell sx={{ width: '60%', borderBottom: 'none' }}>
                        <Conditions data={rule.conditions} />
                      </TableCell>
                      <TableCell
                        sx={{
                          borderBottom: 'none',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1,
                        }}
                      >
                        {rule.ineligibleRiders &&
                        rule.ineligibleRiders.length > 0 ? (
                          rule.ineligibleRiders.map((ineligibleRider, idx) => (
                            <Typography
                              variant="subtitle2"
                              key={`${ruleIdx}-${idx}`}
                            >
                              {ineligibleRider}
                            </Typography>
                          ))
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No ineligible riders
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={2}
                      sx={{ textAlign: 'center', p: 2, borderBottom: 'none' }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        No matching rules found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default RuleAccordion;
